/* ==========================================================================
   Cherry Pot — interaction fixes
   Paired with assets/cp-mobile-fixes.css, loaded from layout/theme.liquid.

   1. Whole product card is clickable, both card types.
   2. Expand button + full-screen player with sound on the video rails.
   3. Scroll dots under the "Shop by category" rail.
   4. Full-screen gallery: thumbnail sync, phone swipe hint, and desktop
      arrows / keyboard / click-to-zoom.

   The video rails are rendered by the Vitals app, which rewrites its own
   section files whenever it syncs. So nothing here edits app markup — it only
   observes it and attaches. Everything is idempotent and guarded by a data
   attribute, because the app re-renders and clones its carousel items.
   ========================================================================== */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 750px)';

  function isDesktop() {
    return window.matchMedia(DESKTOP).matches;
  }

  /* ======================================================================
     1. Whole product card clickable

     Two card types on this store:
       - <product-card>  the theme's shared card: collection pages, search,
                         recommendations, and the Special Bundles section.
       - .cprail-card    ours, used by the homepage tab rails (Best Sellers,
                         New Arrivals, Shop by Budget). Only its image and
                         title were links, so the price row was dead.
     ====================================================================== */

  var CARD_ROOTS = 'product-card, .cprail-card';

  /* Anything a customer might mean to press for its own sake. Taps inside
     these are left alone; everything else on the card navigates. */
  var CARD_INTERACTIVE = [
    'a',
    'button',
    'input',
    'select',
    'textarea',
    'label',
    'summary',
    'details',
    'iframe',
    'video',
    '[role="button"]',
    '[role="link"]',
    'add-to-cart-component',
    'quick-add-component',
    'variant-picker',
    'swatches-variant-picker-component',
    'slideshow-arrows',
    'slideshow-controls',
    '.quick-add',
    '.cpcard-cta',
    '.cpcard-form',
  ].join(',');

  function cardLink(card) {
    return (
      card.querySelector('a.product-card__link[href]') ||
      card.querySelector('a.cprail-card__media[href]') ||
      card.querySelector('a.cprail-card__title[href]') ||
      card.querySelector('a[href*="/products/"]')
    );
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var target = event.target;
    if (!(target instanceof Element)) return;

    var card = target.closest(CARD_ROOTS);
    if (!card) return;
    if (target.closest(CARD_INTERACTIVE)) return;

    /* Don't hijack a tap that was really the end of selecting text. */
    try {
      var selection = window.getSelection();
      if (selection && String(selection).trim().length > 0) return;
    } catch (error) {
      /* no-op */
    }

    var link = cardLink(card);
    if (!link) return;

    link.click();
  });

  /* ======================================================================
     2. Video rails — expand button and full-screen player
     ====================================================================== */

  var EXPAND_ICON =
    '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
    '<path d="M9 4H5a1 1 0 0 0-1 1v4M15 4h4a1 1 0 0 1 1 1v4M9 20H5a1 1 0 0 1-1-1v-4M15 20h4a1 1 0 0 0 1-1v-4" ' +
    'stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/></svg>';

  var lightbox = null;

  function getLightbox() {
    if (lightbox && lightbox.isConnected) return lightbox;

    lightbox = document.createElement('div');
    lightbox.className = 'cp-vlb';
    lightbox.hidden = true;
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', 'Video');

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'cp-vlb__close';
    close.setAttribute('aria-label', 'Close video');
    close.innerHTML = '&times;';
    close.addEventListener('click', closeLightbox);

    var stage = document.createElement('div');
    stage.className = 'cp-vlb__stage';

    lightbox.appendChild(stage);
    lightbox.appendChild(close);

    /* Tapping the backdrop closes; tapping the video itself must not. */
    lightbox.addEventListener('click', function (event) {
      if (event.target === lightbox || event.target === stage) closeLightbox();
    });

    document.body.appendChild(lightbox);
    return lightbox;
  }

  function closeLightbox() {
    if (!lightbox) return;
    var playing = lightbox.querySelector('video');
    if (playing) {
      try {
        playing.pause();
      } catch (error) {
        /* no-op */
      }
    }
    var stage = lightbox.querySelector('.cp-vlb__stage');
    if (stage) stage.textContent = '';
    lightbox.hidden = true;
    document.documentElement.classList.remove('cp-vlb-open');
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && lightbox && !lightbox.hidden) {
      event.preventDefault();
      closeLightbox();
    }
  });

  /**
   * Opens a copy of the given video full screen, with the browser's own
   * controls — play/pause, seek, volume and the native full-screen button —
   * and with sound on, since the rail versions are muted autoplay.
   * @param {HTMLVideoElement} source
   */
  function openVideo(source) {
    var box = getLightbox();
    var stage = box.querySelector('.cp-vlb__stage');
    if (!stage) return;

    stage.textContent = '';

    var video = document.createElement('video');
    video.className = 'cp-vlb__video';
    video.controls = true;
    video.playsInline = true;
    video.loop = true;
    video.preload = 'auto';
    video.setAttribute('controlsList', 'nodownload');
    if (source.poster) video.poster = source.poster;

    var sources = source.querySelectorAll('source');
    if (sources.length) {
      Array.prototype.forEach.call(sources, function (item) {
        var copy = document.createElement('source');
        copy.src = item.src || item.getAttribute('src') || '';
        if (item.type) copy.type = item.type;
        video.appendChild(copy);
      });
    } else if (source.currentSrc || source.src) {
      video.src = source.currentSrc || source.src;
    }

    stage.appendChild(video);

    box.hidden = false;
    document.documentElement.classList.add('cp-vlb-open');

    /* Pause the small one so two copies aren't running. */
    try {
      source.pause();
    } catch (error) {
      /* no-op */
    }

    video.muted = false;
    video.volume = 1;

    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {
        /* Some browsers still refuse sound-on autoplay. Start muted rather
           than showing a frozen first frame; the customer can unmute. */
        video.muted = true;
        var retry = video.play();
        if (retry && typeof retry.catch === 'function') retry.catch(function () {});
      });
    }
  }

  function decorateVideos() {
    var items = document.querySelectorAll('vtls-video-item, .VtlsVideoCarouselBlock__Media');

    Array.prototype.forEach.call(items, function (item) {
      if (item.dataset.cpExpand === 'true') return;

      var video = item.querySelector('video');
      if (!video) return;

      item.dataset.cpExpand = 'true';

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'cp-vexpand';
      button.setAttribute('aria-label', 'Open video in full screen');
      button.innerHTML = EXPAND_ICON;

      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        openVideo(video);
      });

      item.appendChild(button);
    });
  }

  /* ======================================================================
     3. Scroll dots for horizontal rails
     ====================================================================== */

  var RAIL_SELECTOR = '.cp-cat__list';

  function decorateRail(rail) {
    if (rail.dataset.cpDots === 'true') return;
    rail.dataset.cpDots = 'true';

    var dots = document.createElement('div');
    dots.className = 'cp-rail-dots';
    dots.hidden = true;
    if (rail.parentElement) rail.parentElement.classList.add('cp-rail-fade');
    rail.insertAdjacentElement('afterend', dots);

    var frame = null;

    function pageCount() {
      if (!rail.clientWidth) return 1;
      return Math.max(1, Math.round(rail.scrollWidth / rail.clientWidth));
    }

    function render() {
      frame = null;

      var overflow = rail.scrollWidth - rail.clientWidth;

      if (overflow < 12) {
        dots.hidden = true;
        if (rail.parentElement) rail.parentElement.dataset.atEnd = 'true';
        return;
      }

      var pages = pageCount();
      dots.hidden = false;

      if (dots.children.length !== pages) {
        dots.textContent = '';
        for (var i = 0; i < pages; i++) {
          dots.appendChild(makeDot(i));
        }
      }

      var active = Math.min(pages - 1, Math.round(rail.scrollLeft / Math.max(1, rail.clientWidth)));

      Array.prototype.forEach.call(dots.children, function (dot, index) {
        dot.classList.toggle('is-active', index === active);
        dot.setAttribute('aria-current', index === active ? 'true' : 'false');
      });

      if (rail.parentElement) {
        rail.parentElement.dataset.atEnd = rail.scrollLeft >= overflow - 4 ? 'true' : 'false';
      }
    }

    function makeDot(index) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'cp-rail-dots__dot';
      dot.setAttribute('aria-label', 'Show categories, group ' + (index + 1));
      dot.addEventListener('click', function () {
        rail.scrollTo({ left: index * rail.clientWidth, behavior: 'smooth' });
      });
      return dot;
    }

    function schedule() {
      if (frame) return;
      frame = requestAnimationFrame(render);
    }

    rail.addEventListener('scroll', schedule, { passive: true });

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(schedule).observe(rail);
    } else {
      window.addEventListener('resize', schedule);
    }

    render();
  }

  function decorateRails() {
    Array.prototype.forEach.call(document.querySelectorAll(RAIL_SELECTOR), decorateRail);
  }

  /* ======================================================================
     4a. Full-screen gallery — keep the thumbnail strip in step
     The dialog listens for scroll on itself; the photo rail inside it is now
     the thing that scrolls, on both phone and desktop, so relay the event up.
     ====================================================================== */

  document.addEventListener(
    'scroll',
    function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.classList.contains('dialog-zoomed-gallery')) return;

      var dialog = target.closest('dialog');
      if (dialog) dialog.dispatchEvent(new Event('scroll'));
    },
    true
  );

  /* ======================================================================
     4b. Full-screen gallery on desktop — arrows, counter, click-to-zoom

     Stock Horizon stacked the photos at full pixel size and scrolled the
     dialog vertically, so it opened mid-photo. The CSS gives each photo one
     screen, fit inside it; this adds the way around and a way to zoom.

     The photos carry Horizon's declarative on:click="/close". On desktop we
     lift that attribute (the binding is read at click time, so removing it
     disables it) and own the click: on the photo it toggles zoom, on the
     surrounding gutter it closes. On a phone the attribute is put back,
     because tap-to-close is right there and works.
     ====================================================================== */

  var NAV_ICON = {
    prev: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    next: '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };

  function setupZoomDialog(component, dialog, gallery) {
    var prev = makeNav('prev');
    var next = makeNav('next');
    var count = document.createElement('div');
    count.className = 'cp-zoom-count';
    count.hidden = true;

    dialog.appendChild(prev);
    dialog.appendChild(next);
    dialog.appendChild(count);

    var frame = null;
    var drag = null;
    var suppressClick = false;

    function makeNav(which) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'cp-zoom-nav cp-zoom-nav--' + which;
      button.hidden = true;
      button.setAttribute('aria-label', which === 'prev' ? 'Previous photo' : 'Next photo');
      button.innerHTML = NAV_ICON[which];
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        go(which === 'prev' ? -1 : 1);
      });
      return button;
    }

    function slides() {
      return gallery.children;
    }

    function currentIndex() {
      var width = gallery.clientWidth || 1;
      var last = slides().length - 1;
      return Math.max(0, Math.min(last, Math.round(gallery.scrollLeft / width)));
    }

    function unzoomAll() {
      Array.prototype.forEach.call(slides(), function (slide) {
        slide.classList.remove('cp-zoomed', 'cp-dragging');
      });
    }

    function go(direction) {
      unzoomAll();
      var width = gallery.clientWidth || 1;
      var last = slides().length - 1;
      var index = Math.max(0, Math.min(last, currentIndex() + direction));
      gallery.scrollTo({ left: index * width, behavior: 'smooth' });
    }

    function sync() {
      frame = null;
      var total = slides().length;
      var show = isDesktop() && total > 1;

      prev.hidden = !show;
      next.hidden = !show;
      count.hidden = !show;
      if (!show) return;

      var index = currentIndex();
      prev.disabled = index === 0;
      next.disabled = index === total - 1;
      count.textContent = index + 1 + ' / ' + total;
    }

    function schedule() {
      if (frame) return;
      frame = requestAnimationFrame(sync);
    }

    /* Desktop takes the click; a phone keeps Horizon's tap-to-close. */
    function applyMode() {
      var desktop = isDesktop();

      Array.prototype.forEach.call(slides(), function (slide) {
        if (desktop) {
          if (slide.hasAttribute('on:click')) {
            slide.dataset.cpOnClick = slide.getAttribute('on:click');
            slide.removeAttribute('on:click');
          }
        } else {
          if (slide.dataset.cpOnClick && !slide.hasAttribute('on:click')) {
            slide.setAttribute('on:click', slide.dataset.cpOnClick);
          }
          slide.classList.remove('cp-zoomed', 'cp-dragging');
        }
      });

      /* Keep the next arrow and the photo clear of the thumbnail column. */
      var strip = dialog.querySelector('.dialog-thumbnails-list');
      var stripWidth = strip && strip.children.length > 1 ? Math.round(strip.getBoundingClientRect().width) : 0;
      var nextInset = stripWidth ? stripWidth + 12 : 16;

      dialog.style.setProperty('--cp-zoom-next-inset', nextInset + 'px');
      dialog.style.setProperty('--cp-zoom-gutter', Math.max(80, nextInset + 60) + 'px');

      sync();
    }

    function closeDialog() {
      if (typeof component.close === 'function') {
        component.close();
      } else {
        dialog.close();
      }
    }

    function centreZoom(slide) {
      if (!slide.classList.contains('cp-zoomed')) return;
      slide.scrollLeft = (slide.scrollWidth - slide.clientWidth) / 2;
      slide.scrollTop = (slide.scrollHeight - slide.clientHeight) / 2;
    }

    gallery.addEventListener('click', function (event) {
      if (!isDesktop()) return;

      var target = event.target;
      if (!(target instanceof Element)) return;

      var slide = target.closest('.product-media-container');
      if (!slide || slide.parentElement !== gallery) return;

      if (suppressClick) {
        suppressClick = false;
        return;
      }

      /* The photo itself: toggle between fit-to-window and full size. */
      if (target.closest('img.product-media__image') && slide.classList.contains('product-media-container--image')) {
        slide.classList.toggle('cp-zoomed');
        centreZoom(slide);
        return;
      }

      /* The empty gutter around it: close, as clicking away should. */
      if (target === slide) closeDialog();
    });

    /* Drag to pan while zoomed. */
    gallery.addEventListener('pointerdown', function (event) {
      if (!isDesktop() || event.button !== 0) return;
      var target = event.target;
      if (!(target instanceof Element)) return;

      var slide = target.closest('.product-media-container.cp-zoomed');
      if (!slide) return;

      drag = {
        slide: slide,
        x: event.clientX,
        y: event.clientY,
        left: slide.scrollLeft,
        top: slide.scrollTop,
        moved: false,
      };
      slide.classList.add('cp-dragging');
    });

    window.addEventListener('pointermove', function (event) {
      if (!drag) return;
      var dx = event.clientX - drag.x;
      var dy = event.clientY - drag.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
      drag.slide.scrollLeft = drag.left - dx;
      drag.slide.scrollTop = drag.top - dy;
    });

    window.addEventListener('pointerup', function () {
      if (!drag) return;
      drag.slide.classList.remove('cp-dragging');
      if (drag.moved) suppressClick = true;
      drag = null;
    });

    dialog.addEventListener('keydown', function (event) {
      if (!isDesktop()) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(-1);
      }
    });

    gallery.addEventListener('scroll', schedule, { passive: true });

    /* The thumbnail column can only be measured once the dialog is open. */
    new MutationObserver(function () {
      if (dialog.open) {
        unzoomAll();
        applyMode();
      }
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(applyMode, 150);
    });

    applyMode();
  }

  function decorateZoomDialogs() {
    Array.prototype.forEach.call(document.querySelectorAll('zoom-dialog'), function (component) {
      if (component.dataset.cpZoomNav === 'true') return;

      var dialog = component.querySelector('dialog');
      var gallery = component.querySelector('.dialog-zoomed-gallery');
      if (!dialog || !gallery || !gallery.children.length) return;

      component.dataset.cpZoomNav = 'true';
      setupZoomDialog(component, dialog, gallery);
    });
  }

  /* One-time "swipe for more" hint, the first time a customer opens the
     full-screen view on a phone. */
  var hintShown = false;

  document.addEventListener('click', function (event) {
    if (hintShown) return;
    if (isDesktop()) return;

    var target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.closest('.product-media-container--zoomable')) return;

    var gallery = document.querySelector('zoom-dialog .dialog-zoomed-gallery');
    if (!gallery || gallery.children.length < 2) return;

    hintShown = true;

    setTimeout(function () {
      var hint = document.createElement('div');
      hint.className = 'cp-swipe-hint';
      hint.innerHTML = '<span>Swipe for more photos</span>';
      document.body.appendChild(hint);
      setTimeout(function () {
        hint.remove();
      }, 3200);
    }, 350);
  });

  /* ======================================================================
     Boot
     ====================================================================== */

  function run() {
    decorateVideos();
    decorateRails();
    decorateZoomDialogs();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  /* The Vitals rails and Horizon's section re-renders both add nodes after
     load, so re-run on a debounce. Everything above no-ops on second pass. */
  var pending = null;
  var observer = new MutationObserver(function () {
    if (pending) clearTimeout(pending);
    pending = setTimeout(function () {
      pending = null;
      run();
    }, 250);
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();